import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Heart, HandHeart, LogOut, RefreshCw, Check, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [assignedPrayers, setAssignedPrayers] = useState([]);
  const [stats, setStats] = useState({ prayers_completed: 0, active_assignments: 0 });
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prayersRes, statsRes] = await Promise.all([
        axios.get(`${API}/warrior/assigned`, { withCredentials: true }),
        axios.get(`${API}/warrior/stats`, { withCredentials: true })
      ]);
      setAssignedPrayers(prayersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestAssignments = async () => {
    setRequesting(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${API}/warrior/request-assignments`, {}, { withCredentials: true });
      setAssignedPrayers(prev => [...prev, ...data]);
      setStats(prev => ({ ...prev, active_assignments: prev.active_assignments + data.length }));
      setMessage(`${data.length} new prayer${data.length > 1 ? 's' : ''} assigned to you!`);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Error requesting assignments');
    } finally {
      setRequesting(false);
    }
  };

  const completePrayer = async (assignmentId) => {
    setCompletingId(assignmentId);
    try {
      const { data } = await axios.post(`${API}/warrior/complete/${assignmentId}`, {}, { withCredentials: true });
      
      // Remove the completed prayer from list
      setAssignedPrayers(prev => prev.filter(p => p.assignment_id !== assignmentId));
      setStats(prev => ({
        prayers_completed: prev.prayers_completed + 1,
        active_assignments: prev.active_assignments - 1
      }));
      
      // If all prayers were complete and new ones were auto-assigned
      if (data.all_complete && data.new_assignments && data.new_assignments.length > 0) {
        setAssignedPrayers(data.new_assignments);
        setStats(prev => ({
          ...prev,
          active_assignments: data.new_assignments.length
        }));
        setMessage(`All prayers complete! ${data.new_assignments.length} new names have been assigned to you.`);
      } else if (data.all_complete && (!data.new_assignments || data.new_assignments.length === 0)) {
        setMessage('All prayers complete! No more prayer requests available at this time.');
      } else {
        setMessage('Prayer marked as complete. Thank you!');
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Error completing prayer');
    } finally {
      setCompletingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5A6E4B]"></div>
      </div>
    );
  }

  const allComplete = assignedPrayers.length === 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 border-b border-[#E6E2D6]/50">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl font-semibold text-[#2B2E26]" data-testid="dashboard-logo">
            Pray Together
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="hidden md:block font-body text-[#5C6056]" data-testid="user-name">
              Welcome, {user?.name}
            </span>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-12 lg:px-24 py-12" data-testid="warrior-dashboard">
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-6 max-w-md mb-12"
        >
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-[#E6E2D6]">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="w-5 h-5 text-[#C86A51]" />
              <span className="font-body text-[#5C6056] text-sm">Prayers Completed</span>
            </div>
            <p className="font-heading text-4xl font-semibold text-[#2B2E26]" data-testid="prayers-completed-stat">
              {stats.prayers_completed}
            </p>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-[#E6E2D6]">
            <div className="flex items-center gap-3 mb-2">
              <HandHeart className="w-5 h-5 text-[#5A6E4B]" />
              <span className="font-body text-[#5C6056] text-sm">Active Prayers</span>
            </div>
            <p className="font-heading text-4xl font-semibold text-[#2B2E26]" data-testid="active-prayers-stat">
              {stats.active_assignments}
            </p>
          </div>
        </motion.div>

        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-[#5A6E4B]/10 rounded-2xl text-[#5A6E4B] font-body"
            data-testid="dashboard-message"
          >
            {message}
          </motion.div>
        )}

        {/* Section Title */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#2B2E26]" data-testid="assigned-prayers-title">
            Your Assigned Prayers
          </h2>
          {!allComplete && (
            <span className="text-[#5C6056] font-body text-sm">
              {assignedPrayers.length} of 5
            </span>
          )}
        </div>

        {/* Prayer Cards */}
        {allComplete ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 bg-white/50 rounded-2xl border border-[#E6E2D6]"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#5A6E4B]/10 mb-6">
              <Sparkles className="w-10 h-10 text-[#5A6E4B]" />
            </div>
            <h3 className="font-heading text-2xl text-[#2B2E26] mb-3" data-testid="all-complete-title">
              All Prayers Complete!
            </h3>
            <p className="font-body text-[#5C6056] mb-8 max-w-md mx-auto">
              Thank you for your faithfulness. Ready to continue your ministry? Request more names to pray for.
            </p>
            <button
              onClick={requestAssignments}
              disabled={requesting}
              className="btn-accent inline-flex items-center gap-2"
              data-testid="request-more-btn"
            >
              {requesting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Request Next 5 Names
                </>
              )}
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assignedPrayers.map((prayer, index) => (
              <motion.div
                key={prayer.assignment_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="prayer-card bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-[#E6E2D6]"
                data-testid="prayer-card"
              >
                <div className="mb-4">
                  <h3 className="font-heading text-xl text-[#2B2E26] mb-2" data-testid="prayer-person-name">
                    {prayer.person_name}
                  </h3>
                  <p className="font-body text-[#5C6056] text-sm leading-relaxed" data-testid="prayer-intention">
                    {prayer.intention}
                  </p>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-[#E6E2D6]">
                  <span className="text-xs text-[#8A8D86] font-body flex items-center gap-1">
                    <Heart size={12} className="fill-[#C86A51] text-[#C86A51]" />
                    Prayed {prayer.times_prayed} times
                  </span>
                  
                  <button
                    onClick={() => completePrayer(prayer.assignment_id)}
                    disabled={completingId === prayer.assignment_id}
                    className="inline-flex items-center gap-2 bg-[#5A6E4B] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#4C5E3F] transition-colors disabled:opacity-50"
                    data-testid="mark-prayed-btn"
                  >
                    {completingId === prayer.assignment_id ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Mark as Prayed
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Request More Button (when not all complete but less than 5) */}
        {!allComplete && assignedPrayers.length < 5 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 text-center"
          >
            <button
              onClick={requestAssignments}
              disabled={requesting}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="request-more-btn-partial"
            >
              {requesting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Get More Prayers ({5 - assignedPrayers.length} available)
                </>
              )}
            </button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
